import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Popover, PopoverTrigger, PopoverContent } from './ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from './ui/command';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { toast } from 'sonner';
import FileUploader from './FileUploader';
import FileList from './FileList';
import AttachPolicyDialog from './AttachPolicyDialog';
import AIEvidenceDialog from './AIEvidenceDialog';
import EvidenceGuideDialog from './EvidenceGuideDialog';
import TrustCenterInclusionCard from './TrustCenterInclusionCard';
import { AutoEvidencePanel } from './AutoEvidencePanel';
import { CategoryBadgeWithAction } from './CategoryBadgeWithAction';
import NaReasonDialog from './NaReasonDialog';
import { 
  ArrowLeft, 
  Shield, 
  FileText, 
  CheckCircle, 
  Clock, 
  AlertCircle,
  Upload,
  Download,
  ExternalLink,
  Calendar,
  Tag,
  Settings,
  Users,
  Crown,
  Loader2,
  UserPlus,
  UserMinus,
  Trash2,
  AlertTriangle,
  Undo2,
  Plus,
  Eye,
  Edit,
  ScrollText,
  Link2,
  Sparkles,
  RefreshCw,
  Ban,
  RotateCcw,
  Compass,
  Search,
  ChevronDown,
  Check,
} from 'lucide-react';
import { formatDateTime } from '../utils/dateFormatter';
import { formatStatus } from '../utils/formatStatus';
import { Input } from './ui/input';

// Using relative URLs
const API = '/api';

const ControlDetail = ({ controlId: controlIdProp, onClose, isModal: isModalProp } = {}) => {
  const params = useParams();
  const controlId = controlIdProp || params.controlId;
  const isModal = isModalProp ?? Boolean(controlIdProp && onClose);
  const navigate = useNavigate();
  const { returnToAdmin, user } = useAuth();
  const [control, setControl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  const [fileRefreshTrigger, setFileRefreshTrigger] = useState(0);
  
  // Quick Actions state
  const [showAssignReviewer, setShowAssignReviewer] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [selectedReviewer, setSelectedReviewer] = useState('');
  const [ownerPopoverOpen, setOwnerPopoverOpen] = useState(false);
  const [assigningReviewer, setAssigningReviewer] = useState(false);
  const [removingReviewer, setRemovingReviewer] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  
  // Restore state
  const [restoring, setRestoring] = useState(false);
  
  // Policy state
  const [policyExists, setPolicyExists] = useState(true);
  const [recreatingPolicy, setRecreatingPolicy] = useState(false);
  
  // Risks state
  const [risks, setRisks] = useState([]);
  const [loadingRisks, setLoadingRisks] = useState(false);
  const [showAddRiskDialog, setShowAddRiskDialog] = useState(false);
  const [savingRisk, setSavingRisk] = useState(false);
  const [riskForm, setRiskForm] = useState({
    title: '',
    description: '',
    likelihood: 'possible',
    impact: 'moderate',
    status: 'open',
    monitoring_interval: 'monthly',
    owner: '',
    mitigation_plan: ''
  });
  
  // Check if user is auditor (read-only mode)
  const isAuditor = user?.role === 'auditor';
  
  // Check if user is emulated (super admin viewing as customer)
  const isEmulated = localStorage.getItem('emulated_user') !== null;

  useEffect(() => {
    fetchControlData();
    fetchComments();
    fetchRisks();
  }, [controlId]);

  // Central handler for the 8-category action buttons rendered by
  // <CategoryBadgeWithAction>. Each action surfaces in the user's
  // preferred way (toast + nav for connect, scroll-to-section for
  // view-details, etc.) without coupling that component to axios or
  // react-router.
  useEffect(() => {
    const onAction = (e) => {
      const d = e.detail || {};
      if (d.controlId !== controlId) return;
      switch (d.action) {
        case 'retry':
          toast.info('Re-running Auto-Evidence — go to the audit page to track progress.');
          if (control?.audit_id) navigate(`/audits/${control.audit_id}`);
          break;
        case 'connect-integration':
          toast.info(`Opening Connections to add ${d.source === 'github' ? 'GitHub' : 'Google Workspace'}…`);
          navigate('/connections');
          break;
        case 'upload-manual':
          // Scroll to the FileUploader on this page — the easiest
          // affordance for "upload manually" without modal plumbing.
          document.getElementById('manual-upload-anchor')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          break;
        case 'view-details':
          document.getElementById('auto-evidence-panel-anchor')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          break;
        default:
          break;
      }
    };
    window.addEventListener('auto-evidence-action', onAction);
    return () => window.removeEventListener('auto-evidence-action', onAction);
  }, [controlId, control, navigate]);

  const fetchRisks = async () => {
    try {
      setLoadingRisks(true);
      const response = await axios.get(`${API}/controls/${controlId}/risks`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setRisks(response.data.risks || []);
    } catch (error) {
      console.error('Failed to fetch risks:', error);
    } finally {
      setLoadingRisks(false);
    }
  };

  const handleCreateRisk = async () => {
    if (!riskForm.title) {
      toast.error('Please enter a risk title');
      return;
    }

    try {
      setSavingRisk(true);
      await axios.post(`${API}/risks`, {
        ...riskForm,
        audit_id: control.audit_id,
        control_id: controlId
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Risk registered successfully');
      setShowAddRiskDialog(false);
      setRiskForm({
        title: '',
        description: '',
        likelihood: 'possible',
        impact: 'moderate',
        status: 'open',
        monitoring_interval: 'monthly',
        owner: '',
        mitigation_plan: ''
      });
      fetchRisks();
    } catch (error) {
      console.error('Failed to create risk:', error);
      toast.error(error.response?.data?.detail || 'Failed to create risk');
    } finally {
      setSavingRisk(false);
    }
  };

  // Check if linked policy exists
  const checkPolicyExists = async (policyId) => {
    if (!policyId) {
      setPolicyExists(false);
      return;
    }
    try {
      await axios.get(`${API}/policies/${policyId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setPolicyExists(true);
    } catch (error) {
      if (error.response?.status === 404) {
        setPolicyExists(false);
      }
    }
  };

  // Recreate deleted policy
  const handleRecreatePolicy = async () => {
    if (!control?.policy_name) {
      toast.error('No policy name available');
      return;
    }
    
    setRecreatingPolicy(true);
    try {
      const response = await axios.post(`${API}/controls/${controlId}/recreate-policy`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      toast.success('Policy recreated successfully!');
      setPolicyExists(true);
      
      // Update control with new policy_id
      setControl(prev => ({
        ...prev,
        policy_id: response.data.policy_id
      }));
      
      // Open the new policy in a new tab
      if (response.data.policy_id) {
        window.open(`/policies/${response.data.policy_id}`, '_blank');
      }
    } catch (error) {
      console.error('Failed to recreate policy:', error);
      toast.error(error.response?.data?.detail || 'Failed to recreate policy');
    } finally {
      setRecreatingPolicy(false);
    }
  };

  const getRiskLevelColor = (level) => {
    const colors = {
      critical: 'bg-red-600 text-white',
      high: 'bg-orange-500 text-white',
      medium: 'bg-yellow-500 text-white',
      low: 'bg-green-500 text-white'
    };
    return colors[level] || 'bg-gray-500 text-white';
  };

  const getRiskStatusColor = (status) => {
    const colors = {
      open: 'bg-red-100 text-red-700 border-red-200',
      monitoring: 'bg-blue-100 text-blue-700 border-blue-200',
      mitigated: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      resolved: 'bg-green-100 text-green-700 border-green-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  const fetchControlData = async () => {
    try {
      const response = await axios.get(`${API}/controls/${controlId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setControl(response.data);
      setNotes(response.data.notes || '');
      
      // Check if linked policy still exists
      if (response.data.policy_id) {
        checkPolicyExists(response.data.policy_id);
      }
    } catch (error) {
      console.error('Failed to fetch control data:', error);
      
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Access denied. You do not have permission to view this control.');
      } else if (error.response?.status === 404) {
        toast.error('Control not found.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to fetch control data. Please try again.');
      }
      
      // Don't redirect - stay on the page so user can navigate back properly
      setControl(null);
    } finally {
      setLoading(false);
    }
  };

  const updateControlStatus = async (newStatus, naReason = null) => {
    try {
      let url = `${API}/controls/${controlId}/status?status=${newStatus}`;
      if (newStatus === 'not_applicable' && naReason) {
        url += `&na_reason=${encodeURIComponent(naReason)}`;
      }
      await axios.put(url, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setControl({
        ...control,
        status: newStatus,
        ...(newStatus === 'not_applicable' && naReason ? { na_reason: naReason } : {}),
      });
      toast.success('Control status updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update control status');
      console.error('Status update error:', error);
    }
  };

  // Intercept "not_applicable" picks so we can collect a mandatory reason
  // before hitting the server (which also enforces the rule).
  const [statusNaDialog, setStatusNaDialog] = useState(false);
  const handleStatusChange = (newStatus) => {
    if (newStatus === 'not_applicable') {
      setStatusNaDialog(true);
      return;
    }
    updateControlStatus(newStatus);
  };

  const updateControlType = async (newType) => {
    try {
      await axios.put(`${API}/controls/${controlId}/type?control_type=${newType}`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setControl({ ...control, type: newType });
      toast.success(`Collection type updated to ${newType === 'automated' ? 'Auto' : 'Manual'}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update collection type');
      console.error('Type update error:', error);
    }
  };

  const [togglingEvidence, setTogglingEvidence] = useState(null);
  // State for the "mark not required" dialog: { evidence_text } | null
  const [naDialog, setNaDialog] = useState(null);
  const [naRemark, setNaRemark] = useState('');
  // State for the "Link policy" dialog: { evidenceIndex } | null
  const [attachPolicyDialog, setAttachPolicyDialog] = useState(null);
  // State for the AI-generate dialog: { evidenceIndex, evidenceText } | null
  const [aiEvidenceDialog, setAiEvidenceDialog] = useState(null);
  // State for the "How do I collect this?" guide dialog
  const [evidenceGuideDialog, setEvidenceGuideDialog] = useState(null);

  const toggleEvidenceRequirement = async (evidenceText, notRequired, remark = '') => {
    setTogglingEvidence(evidenceText);
    try {
      const body = { evidence_text: evidenceText, not_required: notRequired };
      if (notRequired) body.remark = remark;
      const res = await axios.patch(
        `${API}/controls/${controlId}/evidence-requirements/toggle`,
        body,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
      );
      setControl({
        ...control,
        evidence_not_applicable: res.data?.evidence_not_applicable || [],
      });
      toast.success(notRequired ? 'Marked as not required' : 'Marked as required');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update requirement');
      console.error('Toggle evidence requirement error:', error);
      return false;
    } finally {
      setTogglingEvidence(null);
    }
  };

  // Helper: lookup the not-applicable entry (dict or legacy string) for a given text
  const getNotApplicableEntry = (evidenceText) => {
    const list = control?.evidence_not_applicable || [];
    for (const e of list) {
      if (typeof e === 'string' && e === evidenceText) return { evidence_text: e, remark: '' };
      if (e && typeof e === 'object' && e.evidence_text === evidenceText) return e;
    }
    return null;
  };

  const submitNaDialog = async () => {
    if (!naDialog) return;
    const trimmed = naRemark.trim();
    if (!trimmed) {
      toast.error('Please enter a remark');
      return;
    }
    const ok = await toggleEvidenceRequirement(naDialog.evidence_text, true, trimmed);
    if (ok) {
      setNaDialog(null);
      setNaRemark('');
    }
  };

  const handleDeleteControl = async () => {
    if (deleteConfirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/controls/${controlId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Control deleted successfully');
      setShowDeleteConfirm(false);
      if (isModal && onClose) {
        onClose({ deleted: true, controlId });
      } else {
        // Navigate back to audit
        navigate(`/audit/${control.audit_id}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete control');
      console.error('Delete error:', error);
    } finally {
      setDeleting(false);
    }
  };

  const handleRestoreControl = async () => {
    setRestoring(true);
    try {
      await axios.put(`${API}/controls/${controlId}/restore`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Control restored successfully');
      // Refresh control data to update is_deleted status
      setControl({ ...control, is_deleted: false, deleted_at: null });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore control');
      console.error('Restore error:', error);
    } finally {
      setRestoring(false);
    }
  };
  
  const fetchComments = async () => {
    try {
      const response = await axios.get(`${API}/controls/${controlId}/comments`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setComments(response.data.comments || []);
    } catch (error) {
      console.error('Failed to load comments:', error);
    }
  };
  
  const addComment = async () => {
    if (!newComment.trim()) {
      toast.error('Please enter a comment');
      return;
    }
    
    setLoadingComments(true);
    try {
      await axios.post(
        `${API}/controls/${controlId}/comments?comment=${encodeURIComponent(newComment)}`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      toast.success('Comment added successfully');
      setNewComment('');
      await fetchComments(); // Reload comments
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add comment');
    } finally {
      setLoadingComments(false);
    }
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      // In a real app, you'd have an endpoint to save notes
      // For now, we'll just show a success message
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
      toast.success('Notes saved successfully');
    } catch (error) {
      toast.error('Failed to save notes');
    } finally {
      setSavingNotes(false);
    }
  };

  const getFrameworkColor = (framework) => {
    const colors = {
      'SOC 2': 'bg-blue-100 text-blue-700 border-blue-200',
      'GDPR': 'bg-red-100 text-red-700 border-red-200',
      'ISO 27001': 'bg-purple-100 text-purple-700 border-purple-200',
      'PCI DSS': 'bg-orange-100 text-orange-700 border-orange-200'
    };
    return colors[framework] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-700',
      'in_review': 'bg-blue-100 text-blue-700',
      'completed': 'bg-green-100 text-green-700',
      'not_applicable': 'bg-slate-100 text-slate-500'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const getTypeColor = (type) => {
    return type === 'automated' 
      ? 'bg-emerald-100 text-navy-700 border-emerald-200'
      : 'bg-slate-100 text-slate-700 border-slate-200';
  };

  const getTypeLabel = (type) => {
    if (type === 'automated') return 'Auto';
    return formatStatus(type);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'in_review':
        return <Clock className="w-5 h-5 text-blue-600" />;
      case 'not_applicable':
        return <AlertCircle className="w-5 h-5 text-slate-400" />;
      default:
        return <AlertCircle className="w-5 h-5 text-yellow-600" />;
    }
  };

  // Quick Action: Export Control Details as Excel
  const handleExportDetails = async () => {
    if (!control) return;
    
    setExporting(true);
    try {
      // Create control details sheet data
      const controlData = [
        ['Control Details Report'],
        [''],
        ['Field', 'Value'],
        ['Control Reference', control.control_ref || ''],
        ['Control ID', control.id || ''],
        ['Control Name', control.control_name || control.name || ''],
        ['Description', control.description || ''],
        ['Framework', control.framework || ''],
        ['Category', control.category || ''],
        ['Status', control.status || ''],
        ['Type', getTypeLabel(control.type) || ''],
        ['Owner', control.reviewer_email || 'Not assigned'],
        ['Created At', control.created_at ? formatDateTime(control.created_at) : ''],
        [''],
        ['Evidence Requirements'],
      ];
      
      // Add evidence requirements
      if (control.evidence_required && control.evidence_required.length > 0) {
        control.evidence_required.forEach((evidence, index) => {
          controlData.push([`${index + 1}.`, evidence]);
        });
      } else {
        controlData.push(['', 'No evidence requirements specified']);
      }
      
      // Add comments section
      controlData.push(['']);
      controlData.push(['Comments']);
      controlData.push(['Date', 'User', 'Comment']);
      
      if (comments && comments.length > 0) {
        comments.forEach(comment => {
          controlData.push([
            comment.created_at ? formatDateTime(comment.created_at) : '',
            comment.user_name || comment.user_email || '',
            comment.comment || ''
          ]);
        });
      } else {
        controlData.push(['', '', 'No comments']);
      }
      
      // Add export metadata
      controlData.push(['']);
      controlData.push(['Export Information']);
      controlData.push(['Exported At', new Date().toLocaleString()]);
      controlData.push(['Exported By', user?.email || 'Unknown']);
      
      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_array ? XLSX.utils.aoa_to_sheet(controlData) : XLSX.utils.aoa_to_sheet(controlData);
      
      // Set column widths
      ws['!cols'] = [
        { wch: 20 }, // Column A
        { wch: 60 }, // Column B
        { wch: 40 }, // Column C
      ];
      
      XLSX.utils.book_append_sheet(wb, ws, 'Control Details');
      
      // Generate filename
      const filename = `control-${(control.control_name || control.name || control.id).replace(/[^a-z0-9]/gi, '-').toLowerCase()}-export.xlsx`;
      
      // Download
      XLSX.writeFile(wb, filename);
      
      toast.success('Control details exported to Excel');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export control details');
    } finally {
      setExporting(false);
    }
  };

  // Quick Action: View in Audit
  const handleViewInAudit = () => {
    if (control?.audit_id) {
      if (isModal && onClose) {
        onClose({ navigated: true });
      }
      navigate(`/audit/${control.audit_id}`);
    } else {
      toast.error('No audit associated with this control');
    }
  };

  // Quick Action: Fetch employees for reviewer assignment.
  // Only ACTIVE human employees can be assigned as control owners —
  // exited / non-employee statuses, non_employee/distribution_list identity
  // types, and auditors are all excluded. We ask the API to filter to
  // identity_type=employee and then belt-and-braces filter again on the
  // client for legacy rows that pre-date classification.
  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        params: { identity_type: 'employee' },
      });
      const all = response.data.employees || response.data || [];
      const activeEmployees = all.filter((e) => {
        const status = (e?.status || 'active').toLowerCase();
        const idType = (e?.identity_type || 'employee').toLowerCase();
        return status === 'active' && idType === 'employee';
      });
      setEmployees(activeEmployees);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
      toast.error('Failed to load team members');
    }
  };

  // Quick Action: Open Assign Reviewer dialog
  const handleOpenAssignReviewer = async () => {
    await fetchEmployees();
    setSelectedReviewer(control?.reviewer_id || '');
    setOwnerPopoverOpen(false);
    setShowAssignReviewer(true);
  };

  // Quick Action: Assign Reviewer
  const handleAssignReviewer = async () => {
    if (!selectedReviewer) {
      toast.error('Please select an owner');
      return;
    }
    
    setAssigningReviewer(true);
    try {
      await axios.put(
        `${API}/controls/${controlId}/reviewer`,
        { reviewer_id: selectedReviewer },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      // Update local state
      const reviewer = employees.find(e => e.id === selectedReviewer);
      setControl({
        ...control,
        reviewer_id: selectedReviewer,
        reviewer_email: reviewer?.email,
        reviewer_name: reviewer?.full_name || reviewer?.name
      });
      
      toast.success('Owner assigned successfully');
      setShowAssignReviewer(false);
    } catch (error) {
      console.error('Assign reviewer error:', error);
      toast.error(error.response?.data?.detail || 'Failed to assign owner');
    } finally {
      setAssigningReviewer(false);
    }
  };

  // Quick Action: Remove Reviewer
  const handleRemoveReviewer = async () => {
    setRemovingReviewer(true);
    try {
      await axios.delete(
        `${API}/controls/${controlId}/reviewer`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      // Update local state
      setControl({
        ...control,
        reviewer_id: null,
        reviewer_email: null,
        reviewer_name: null
      });
      setSelectedReviewer('');
      
      toast.success('Owner removed successfully');
      setShowAssignReviewer(false);
    } catch (error) {
      console.error('Remove reviewer error:', error);
      toast.error(error.response?.data?.detail || 'Failed to remove reviewer');
    } finally {
      setRemovingReviewer(false);
    }
  };

  // Handle general file upload from Quick Actions
  const handleQuickUploadComplete = () => {
    setFileRefreshTrigger(prev => prev + 1);
    toast.success('Evidence uploaded successfully');
  };

  if (loading) {
    return (
      <div className={isModal ? "bg-appbg" : "min-h-screen bg-appbg"}>
        <div className={`flex items-center justify-center ${isModal ? 'h-96' : 'min-h-screen'}`}>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy-600"></div>
        </div>
      </div>
    );
  }

  if (!control) {
    return (
      <div className={`${isModal ? '' : 'min-h-screen'} bg-appbg flex items-center justify-center ${isModal ? 'h-96' : ''}`}>
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Control Not Found</h2>
          <p className="text-slate-600 mb-4">The requested control could not be found.</p>
          <Button onClick={() => (isModal && onClose ? onClose() : navigate('/'))} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            {isModal ? 'Close' : 'Back to Dashboard'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={isModal ? "bg-appbg" : "min-h-screen bg-appbg"}>
      {/* Header */}
      <div className={`bg-white/80 backdrop-blur-xl border-b border-slate-200 ${isModal ? '' : 'sticky top-0 z-50'}`}>
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => (isModal && onClose ? onClose() : navigate(-1))}
                className="flex items-center space-x-2"
                data-testid="back-button"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>{isModal ? 'Close' : 'Back'}</span>
              </Button>
              
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Control Detail</h1>
                <p className="text-sm text-slate-600">Review and manage compliance control</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              {getStatusIcon(control.status)}
              <Badge className={getStatusColor(control.status)}>
                {formatStatus(control.status)}
                {isEmulated && <span className="ml-2 text-purple-600">• Emulated</span>}
              </Badge>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Deleted Control Banner */}
        {control?.is_deleted && (
          <div className="mb-6 animate-fadeIn">
            <Card className="border-2 border-red-300 bg-red-50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                      <Trash2 className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-red-700">This control has been deleted</p>
                      <div className="text-sm text-red-600 space-y-1">
                        {control.deleted_at && (
                          <p>Deleted on {formatDateTime(control.deleted_at)}</p>
                        )}
                        {(control.deleted_by_name || control.deleted_by_email) && (
                          <p className="flex items-center space-x-1 flex-wrap">
                            <span>Deleted by:</span>
                            <span className="font-medium">
                              {control.deleted_by_name || 'Unknown'}
                            </span>
                            {control.deleted_by_email && (
                              <span className="text-red-500">({control.deleted_by_email})</span>
                            )}
                          </p>
                        )}
                        {/* Show emulated super admin info if present */}
                        {control.emulated_by && (control.emulated_by_name || control.emulated_by_email) && (
                          <p className="flex items-center space-x-1 flex-wrap bg-orange-100 text-orange-700 rounded px-2 py-1 mt-1">
                            <Crown className="w-3 h-3" />
                            <span>Emulated by Super Admin:</span>
                            <span className="font-medium">
                              {control.emulated_by_name || 'Unknown'}
                            </span>
                            {control.emulated_by_email && (
                              <span className="text-orange-600">({control.emulated_by_email})</span>
                            )}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  <Button 
                    onClick={handleRestoreControl}
                    disabled={restoring}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    {restoring ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Restoring...
                      </>
                    ) : (
                      <>
                        <Undo2 className="w-4 h-4 mr-2" />
                        Restore Control
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Control Overview */}
        <div className="mb-8 animate-fadeIn">
          <Card className="border-0 shadow-xl bg-white/90 backdrop-blur-sm">
            <CardHeader className="pb-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-navy-600 to-navy-700 rounded-xl flex items-center justify-center">
                      <Shield className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      {control.control_ref && (
                        <div className="mb-2">
                          <span className="text-sm font-mono text-purple-600 bg-purple-50 px-2 py-1 rounded border border-purple-200">
                            {control.control_ref}
                          </span>
                        </div>
                      )}
                      <CardTitle className="text-2xl mb-2">{control.control_name}</CardTitle>
                      <div className="flex items-center space-x-3">
                        <Badge 
                          variant="outline"
                          className={`${getFrameworkColor(control.framework)} font-medium`}
                        >
                          {control.framework}
                        </Badge>
                        <Badge 
                          variant="outline"
                          className={`${getTypeColor(control.type)} font-medium`}
                        >
                          {getTypeLabel(control.type)}
                        </Badge>
                        <div className="flex items-center space-x-1 text-sm text-slate-600">
                          <Tag className="w-4 h-4" />
                          <span>{control.category}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex flex-col items-end space-y-3">
                  <Select 
                    value={control.status} 
                    onValueChange={handleStatusChange}
                  >
                    <SelectTrigger className={`w-48 ${getStatusColor(control.status)}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">
                        <div className="flex items-center space-x-2">
                          <AlertCircle className="w-4 h-4 text-yellow-600" />
                          <span>Pending</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="in_review">
                        <div className="flex items-center space-x-2">
                          <Clock className="w-4 h-4 text-blue-600" />
                          <span>In Review</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="completed">
                        <div className="flex items-center space-x-2">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          <span>Completed</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="not_applicable">
                        <div className="flex items-center space-x-2">
                          <AlertCircle className="w-4 h-4 text-slate-400" />
                          <span>Not Applicable</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  
                  <div className="text-right text-sm text-slate-600">
                    <div className="flex items-center space-x-1">
                      <Calendar className="w-4 h-4" />
                      <span>Created {formatDateTime(control.created_at)}</span>
                    </div>
                  </div>
                </div>
              </div>
              {control?.status === 'not_applicable' && control?.na_reason && (
                <div
                  className="mt-4 p-3 bg-slate-50 border border-slate-200 rounded-lg text-sm"
                  data-testid="control-na-reason"
                >
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs uppercase tracking-wide text-slate-500 font-medium mb-1">
                        Not applicable — reason
                      </p>
                      <p className="text-slate-700 whitespace-pre-wrap break-words">{control.na_reason}</p>
                      {(control.na_reason_by_name || control.na_reason_by_email || control.na_reason_at) && (
                        <p className="text-xs text-slate-500 mt-1.5">
                          Marked by {control.na_reason_by_name || control.na_reason_by_email || 'a teammate'}
                          {control.na_reason_at ? ` · ${formatDateTime(control.na_reason_at)}` : ''}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </CardHeader>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8 animate-slideIn">
            {/* Description */}
            <Card className="border-0 shadow-soft bg-white/80 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <FileText className="w-5 h-5 text-navy-600" />
                  <span>Control Description</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-700 leading-relaxed">
                  {control.description}
                </p>
              </CardContent>
            </Card>

            {/* Trust Center inclusion — shortcut so admins can opt this
                control in/out of the public Trust Center without leaving
                the control detail view. Full access & ordering settings
                remain in the Trust Center Admin. */}
            <TrustCenterInclusionCard
              type="control"
              id={control.id}
              name={control.control_name || control.name}
            />

            {/* Auto-Collected Evidence — only shown for auto-collectable (Auto) controls */}
            {control.type === 'automated' && (
              <AutoEvidencePanel
                control={control}
                onEvaluated={(auto) => setControl({ ...control, auto_evidence: auto, auto_evidence_status: auto.status })}
              />
            )}

            {/* Evidence Requirements */}
            <Card className="border-0 shadow-soft bg-white/80 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <CheckCircle className="w-5 h-5 text-blue-600" />
                  <span>Evidence Requirements</span>
                </CardTitle>
                <CardDescription>
                  Documentation and evidence needed to demonstrate compliance
                </CardDescription>
              </CardHeader>
              <CardContent>
                {(() => {
                  const autoEv = control.auto_evidence;
                  const autoPass = autoEv?.status === 'pass';
                  const autoInsufficient = autoEv?.status === 'insufficient_evidence';
                  const autoFail = autoEv?.status === 'fail';
                  return (
                <div className="space-y-4">
                  {control.evidence_required && control.evidence_required.length > 0 ? (
                    control.evidence_required.map((evidence, index) => {
                      const isAutoCollectable = control.type === 'automated';
                      const naEntry = getNotApplicableEntry(evidence);
                      const isNotRequired = !!naEntry;
                      // Only treat THIS item as auto-covered when the matcher
                      // says the signal applies to it (prevents one verdict
                      // being broadcast across unrelated required items).
                      const applicableIdx = autoEv?.applicable_evidence_indices;
                      const itemApplicable = !Array.isArray(applicableIdx) || applicableIdx.includes(index);
                      const autoSatisfied = !isNotRequired && isAutoCollectable && autoPass && itemApplicable;
                      const isToggling = togglingEvidence === evidence;
                      return (
                      <div
                        key={index}
                        className={`p-4 rounded-lg border ${
                          isNotRequired
                            ? 'bg-slate-100/70 border-slate-200 opacity-75'
                            : autoSatisfied
                            ? 'bg-emerald-50/60 border-emerald-200'
                            : 'bg-slate-50 border-slate-200'
                        }`}
                        data-testid={`evidence-requirement-${index}`}
                      >
                        <div className="flex items-start space-x-3">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                            isNotRequired ? 'bg-slate-200' : autoSatisfied ? 'bg-emerald-100' : 'bg-blue-100'
                          }`}>
                            {isNotRequired ? (
                              <Ban className="w-4 h-4 text-slate-500" />
                            ) : autoSatisfied ? (
                              <CheckCircle className="w-4 h-4 text-emerald-600" />
                            ) : (
                              <span className="text-xs font-medium text-blue-600">{index + 1}</span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`font-medium mb-2 break-words ${
                              isNotRequired ? 'text-slate-500 line-through' : 'text-slate-700'
                            }`}>{evidence}</p>
                            <div className="flex items-center gap-2 mb-3 flex-wrap">
                              {isNotRequired ? (
                                <Badge className="bg-slate-200 text-slate-700 border-slate-300 text-xs">
                                  Not required
                                </Badge>
                              ) : (
                                // Feb 2026 — rely on the control-level
                                // auto_evidence.category as the single
                                // source of truth. The matcher + sentinel
                                // post-processor guarantees every
                                // evaluated control has one of the 8
                                // categories, so we no longer guess from
                                // `control.type === 'automated'` (which
                                // could lie when no collector exists).
                                <CategoryBadgeWithAction
                                  control={control}
                                  autoEv={autoEv}
                                  evidenceLabel={evidence}
                                  applicable={itemApplicable}
                                />
                              )}
                            </div>

                            {/* Remark for not-required */}
                            {isNotRequired && naEntry?.remark && (
                              <div
                                className="mt-1 mb-3 px-3 py-2 rounded-md bg-white/70 border border-slate-200 text-xs text-slate-600 whitespace-pre-wrap"
                                data-testid={`evidence-na-remark-${index}`}
                              >
                                <span className="font-semibold text-slate-700">Remark:</span>{' '}
                                {naEntry.remark}
                                {naEntry.marked_by_email && (
                                  <span className="block mt-1 text-[11px] text-slate-500">
                                    — {naEntry.marked_by_email}
                                    {naEntry.marked_at && (
                                      <> · {new Date(naEntry.marked_at).toLocaleString()}</>
                                    )}
                                  </span>
                                )}
                              </div>
                            )}

                            {/* Inline uploaded files for this requirement */}
                            {!isNotRequired && (
                              <div className="mt-3 border-t border-slate-200 pt-3">
                                <FileList
                                  auditId={control.audit_id}
                                  controlId={controlId}
                                  evidenceIndex={index}
                                  refreshTrigger={fileRefreshTrigger}
                                  compact={true}
                                  showEmpty={false}
                                />
                              </div>
                            )}
                          </div>
                          {!isAuditor && (
                            <div className="flex flex-col items-end gap-2 flex-shrink-0">
                              {!isNotRequired && (
                                <FileUploader
                                  auditId={control.audit_id}
                                  controlId={controlId}
                                  evidenceIndex={index}
                                  onUploadComplete={() => setFileRefreshTrigger(prev => prev + 1)}
                                  buttonVariant="outline"
                                  buttonSize="sm"
                                  buttonText={autoSatisfied ? 'Add more' : 'Upload'}
                                />
                              )}
                              {!isNotRequired && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setAttachPolicyDialog({ evidenceIndex: index })}
                                  className="text-xs text-navy-700 border-navy-200 hover:bg-navy-50"
                                  data-testid={`evidence-link-policy-btn-${index}`}
                                >
                                  <Link2 className="w-3 h-3 mr-1" />
                                  Link policy
                                </Button>
                              )}
                              {!isNotRequired && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setAiEvidenceDialog({
                                    evidenceIndex: index,
                                    evidenceText: evidence,
                                  })}
                                  className="text-xs text-violet-700 border-violet-300 hover:bg-violet-50"
                                  data-testid={`evidence-ai-generate-btn-${index}`}
                                >
                                  <Sparkles className="w-3 h-3 mr-1" />
                                  Generate with AI
                                </Button>
                              )}
                              {!isNotRequired && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setEvidenceGuideDialog({
                                    evidenceIndex: index,
                                    evidenceText: evidence,
                                  })}
                                  className="text-xs text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                                  data-testid={`evidence-how-to-collect-btn-${index}`}
                                >
                                  <Compass className="w-3 h-3 mr-1" />
                                  How do I collect this?
                                </Button>
                              )}
                              {isNotRequired ? (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => toggleEvidenceRequirement(evidence, false)}
                                  disabled={isToggling}
                                  className="text-xs text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                                  data-testid={`evidence-mark-required-btn-${index}`}
                                >
                                  {isToggling ? (
                                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                  ) : (
                                    <RotateCcw className="w-3 h-3 mr-1" />
                                  )}
                                  Mark as required
                                </Button>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setNaRemark('');
                                    setNaDialog({ evidence_text: evidence });
                                  }}
                                  disabled={isToggling}
                                  className="text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                                  data-testid={`evidence-mark-not-required-btn-${index}`}
                                >
                                  {isToggling ? (
                                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                  ) : (
                                    <Ban className="w-3 h-3 mr-1" />
                                  )}
                                  Mark not required
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      );
                    })
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No evidence requirements specified</p>
                    </div>
                  )}
                </div>
                  );
                })()}
              </CardContent>
            </Card>

            {/* Linked Policy Section */}
            {(control.requires_policy || control.policy_id) && (
              <Card className="border-0 shadow-soft bg-white/80 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <ScrollText className="w-5 h-5 text-purple-600" />
                    <span>Related Policy</span>
                  </CardTitle>
                  <CardDescription>
                    Policy document required for this control
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {control.policy_id && policyExists ? (
                    <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                            <ScrollText className="w-5 h-5 text-purple-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{control.policy_name || 'Linked Policy'}</p>
                            <div className="flex items-center space-x-2 mt-1">
                              <Link2 className="w-3 h-3 text-purple-500" />
                              <span className="text-xs text-purple-600">Policy linked to this control</span>
                            </div>
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(`/policies/${control.policy_id}`, '_blank')}
                          className="border-purple-300 text-purple-700 hover:bg-purple-100"
                        >
                          <ExternalLink className="w-4 h-4 mr-1" />
                          View Policy
                        </Button>
                      </div>
                    </div>
                  ) : control.policy_id && !policyExists ? (
                    <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <AlertTriangle className="w-5 h-5 text-red-600" />
                          <div>
                            <p className="font-medium text-red-800">Policy Not Found</p>
                            <p className="text-sm text-red-700 mt-1">
                              The linked policy "{control.policy_name}" was deleted. Would you like to recreate it?
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="default"
                          size="sm"
                          onClick={handleRecreatePolicy}
                          disabled={recreatingPolicy}
                          className="bg-purple-600 hover:bg-purple-700 text-white"
                        >
                          {recreatingPolicy ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                              Creating...
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-4 h-4 mr-1" />
                              Recreate Policy
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                      <div className="flex items-center space-x-3">
                        <AlertCircle className="w-5 h-5 text-amber-600" />
                        <div>
                          <p className="font-medium text-amber-800">Policy Required</p>
                          <p className="text-sm text-amber-700 mt-1">
                            This control requires a policy document but none has been linked yet.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* General Uploaded Files Section (for files without specific evidence index) */}
            <Card className="border-0 shadow-soft bg-white/80 backdrop-blur-sm">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                <div>
                  <CardTitle className="flex items-center space-x-2">
                    <Download className="w-5 h-5 text-green-600" />
                    <span>Additional Evidence</span>
                  </CardTitle>
                  <CardDescription>
                    General evidence files not linked to specific requirements
                  </CardDescription>
                </div>
                {!isAuditor && control?.audit_id && (
                  <FileUploader
                    auditId={control.audit_id}
                    controlId={controlId}
                    onUploadComplete={() => setFileRefreshTrigger(prev => prev + 1)}
                    buttonVariant="outline"
                    buttonSize="sm"
                    buttonText="Upload Evidence"
                  />
                )}
              </CardHeader>
              <CardContent>
                {control?.audit_id ? (
                  <FileList 
                    auditId={control.audit_id}
                    controlId={controlId}
                    refreshTrigger={fileRefreshTrigger}
                    compact={false}
                    generalOnly={true}
                  />
                ) : (
                  <p className="text-sm text-slate-500">No audit associated with this control</p>
                )}
              </CardContent>
            </Card>

            {/* Risks Section */}
            <Card className="border-0 shadow-soft bg-white/80 backdrop-blur-sm">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center space-x-2">
                    <AlertTriangle className="w-5 h-5 text-orange-600" />
                    <span>Associated Risks</span>
                    {risks.length > 0 && (
                      <Badge variant="outline" className="ml-2">{risks.length}</Badge>
                    )}
                  </CardTitle>
                  {!isAuditor && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setShowAddRiskDialog(true)}
                      data-testid="add-risk-btn"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Risk
                    </Button>
                  )}
                </div>
                <CardDescription>
                  Risks identified for this control
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loadingRisks ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                  </div>
                ) : risks.length === 0 ? (
                  <div className="text-center py-6">
                    <Shield className="w-10 h-10 text-green-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">No risks identified</p>
                    {!isAuditor && (
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => setShowAddRiskDialog(true)}
                        className="mt-2 text-navy-600"
                      >
                        Register a risk
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {risks.map((risk) => (
                      <div 
                        key={risk.id} 
                        className="border border-slate-200 rounded-lg p-3 hover:bg-slate-50 cursor-pointer transition-colors"
                        onClick={() => navigate('/risks')}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge className={getRiskLevelColor(risk.risk_level)}>
                                {risk.risk_level?.toUpperCase()}
                              </Badge>
                              <Badge variant="outline" className={getRiskStatusColor(risk.status)}>
                                {risk.status}
                              </Badge>
                            </div>
                            <p className="font-medium text-slate-900 text-sm">{risk.title}</p>
                            {risk.description && (
                              <p className="text-xs text-slate-500 mt-1 line-clamp-2">{risk.description}</p>
                            )}
                          </div>
                          <ExternalLink className="w-4 h-4 text-slate-400 flex-shrink-0 ml-2" />
                        </div>
                        {risk.owner && (
                          <div className="flex items-center gap-1 mt-2 text-xs text-slate-500">
                            <Users className="w-3 h-3" />
                            <span>Owner: {risk.owner}</span>
                          </div>
                        )}
                      </div>
                    ))}
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => navigate('/risks')}
                      className="w-full text-navy-600"
                    >
                      View all risks in Risk Register →
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Add Risk Dialog */}
            <Dialog open={showAddRiskDialog} onOpenChange={setShowAddRiskDialog}>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Register New Risk</DialogTitle>
                  <DialogDescription>
                    Associate a risk with this control
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div>
                    <Label className="text-sm font-medium">Title *</Label>
                    <Input
                      value={riskForm.title}
                      onChange={(e) => setRiskForm(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="Risk title"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Description</Label>
                    <Textarea
                      value={riskForm.description}
                      onChange={(e) => setRiskForm(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Describe the risk"
                      rows={3}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium">Likelihood</Label>
                      <Select value={riskForm.likelihood} onValueChange={(v) => setRiskForm(prev => ({ ...prev, likelihood: v }))}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="rare">Rare</SelectItem>
                          <SelectItem value="unlikely">Unlikely</SelectItem>
                          <SelectItem value="possible">Possible</SelectItem>
                          <SelectItem value="likely">Likely</SelectItem>
                          <SelectItem value="almost_certain">Almost Certain</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Impact</Label>
                      <Select value={riskForm.impact} onValueChange={(v) => setRiskForm(prev => ({ ...prev, impact: v }))}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="insignificant">Insignificant</SelectItem>
                          <SelectItem value="minor">Minor</SelectItem>
                          <SelectItem value="moderate">Moderate</SelectItem>
                          <SelectItem value="major">Major</SelectItem>
                          <SelectItem value="severe">Severe</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Owner</Label>
                    <Input
                      value={riskForm.owner}
                      onChange={(e) => setRiskForm(prev => ({ ...prev, owner: e.target.value }))}
                      placeholder="Person responsible"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Mitigation Plan</Label>
                    <Textarea
                      value={riskForm.mitigation_plan}
                      onChange={(e) => setRiskForm(prev => ({ ...prev, mitigation_plan: e.target.value }))}
                      placeholder="Steps to mitigate this risk"
                      rows={2}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowAddRiskDialog(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleCreateRisk}
                    disabled={savingRisk}
                    className="bg-navy-600 hover:bg-navy-700"
                  >
                    {savingRisk ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : 'Register Risk'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Comments Section */}
            <Card className="border-0 shadow-soft bg-white/80 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <FileText className="w-5 h-5 text-navy-600" />
                  <span>Comments & Notes</span>
                </CardTitle>
                <CardDescription>
                  {isAuditor ? 
                    "Add your notes and findings" : 
                    "View comments and add internal notes"}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Add New Comment */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Add Comment</label>
                  <Textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Add your comments, findings, or notes..."
                    rows={4}
                    className="resize-none"
                  />
                  <div className="flex justify-end">
                    <Button 
                      onClick={addComment} 
                      disabled={loadingComments || !newComment.trim()}
                      size="sm"
                      className="bg-navy-600 hover:bg-navy-700 text-white"
                    >
                      {loadingComments ? 'Adding...' : 'Add Comment'}
                    </Button>
                  </div>
                </div>
                
                {/* Comments History */}
                <div className="mt-6">
                  <h4 className="text-sm font-semibold text-slate-700 mb-3">Comment History</h4>
                  {comments.length === 0 ? (
                    <p className="text-sm text-slate-500 italic">No comments yet</p>
                  ) : (
                    <div className="space-y-3">
                      {comments.map((comment, index) => (
                        <div key={index} className="border-l-4 border-navy-500 bg-slate-50 p-3 rounded-r-lg">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center space-x-2">
                              <Users className="w-4 h-4 text-navy-600" />
                              <span className="text-sm font-medium text-slate-900">
                                {comment.user_name || comment.user_email}
                              </span>
                              {comment.user_id === user?.id && (
                                <Badge variant="outline" className="text-xs">You</Badge>
                              )}
                            </div>
                            <div className="flex items-center space-x-1 text-xs text-slate-500">
                              <Calendar className="w-3 h-3" />
                              <span>{formatDateTime(comment.created_at)}</span>
                            </div>
                          </div>
                          <p className="text-sm text-slate-700 whitespace-pre-wrap">{comment.comment}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6 animate-slideIn">
            {/* Quick Actions */}
            <Card className="border-0 shadow-soft bg-white/80 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-lg">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Upload Evidence - using FileUploader component */}
                {!isAuditor && control?.audit_id ? (
                  <FileUploader
                    auditId={control.audit_id}
                    controlId={controlId}
                    onUploadComplete={handleQuickUploadComplete}
                    buttonVariant="outline"
                    buttonSize="sm"
                    buttonText="Upload Evidence"
                    buttonClassName="w-full justify-start"
                    buttonIcon={<Upload className="w-4 h-4 mr-2" />}
                  />
                ) : (
                  <Button 
                    variant="outline" 
                    className="w-full justify-start" 
                    size="sm"
                    disabled
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Evidence
                  </Button>
                )}
                
                <Button 
                  variant="outline" 
                  className="w-full justify-start" 
                  size="sm"
                  onClick={handleExportDetails}
                  disabled={exporting}
                >
                  {exporting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  {exporting ? 'Exporting...' : 'Export Details'}
                </Button>
                
                <Button 
                  variant="outline" 
                  className="w-full justify-start" 
                  size="sm"
                  onClick={handleViewInAudit}
                  disabled={!control?.audit_id}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  View in Audit
                </Button>
                
                <Button 
                  variant="outline" 
                  className="w-full justify-start" 
                  size="sm"
                  onClick={handleOpenAssignReviewer}
                  disabled={isAuditor}
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  {control?.reviewer_email ? 'Change Owner' : 'Assign Owner'}
                </Button>
                
                {control?.reviewer_email && (
                  <div className="pt-2 border-t border-slate-200">
                    <p className="text-xs text-slate-500">Current Owner:</p>
                    <p className="text-sm font-medium text-slate-700">{control.reviewer_name || control.reviewer_email}</p>
                    {control?.owner_needs_reassignment && (
                      <div className="mt-2 flex items-start gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5" data-testid="control-owner-needs-reassignment">
                        <UserMinus className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                        <span>Owner is no longer an active team member. Please reassign.</span>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Delete/Restore Control */}
                <div className="pt-3 mt-3 border-t border-slate-200">
                  {control?.is_deleted ? (
                    <Button 
                      variant="outline" 
                      className="w-full justify-start text-green-600 border-green-200 hover:bg-green-50 hover:border-green-300"
                      onClick={handleRestoreControl}
                      disabled={isAuditor || restoring}
                    >
                      {restoring ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Restoring...
                        </>
                      ) : (
                        <>
                          <Undo2 className="w-4 h-4 mr-2" />
                          Restore Control
                        </>
                      )}
                    </Button>
                  ) : (
                    <Button 
                      variant="outline" 
                      className="w-full justify-start text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
                      onClick={() => setShowDeleteConfirm(true)}
                      disabled={isAuditor}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Control
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Control Metadata */}
            <Card className="border-0 shadow-soft bg-white/80 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-lg">Control Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-sm font-medium text-slate-700">Framework</Label>
                  <div className="mt-1">
                    <Badge 
                      variant="outline"
                      className={`${getFrameworkColor(control.framework)}`}
                    >
                      {control.framework}
                    </Badge>
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-medium text-slate-700">Category</Label>
                  <p className="text-sm text-slate-900 mt-1 font-medium">{control.category}</p>
                </div>

                <div>
                  <Label className="text-sm font-medium text-slate-700">Collection Type</Label>
                  <div className="mt-1">
                    <Select 
                      value={control.type} 
                      onValueChange={updateControlType}
                    >
                      <SelectTrigger className={`w-36 ${getTypeColor(control.type)}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="automated">
                          <div className="flex items-center space-x-2">
                            <Settings className="w-4 h-4 text-blue-600" />
                            <span>Auto</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="manual">
                          <div className="flex items-center space-x-2">
                            <Users className="w-4 h-4 text-purple-600" />
                            <span>Manual</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-medium text-slate-700">Evidence Count</Label>
                  <p className="text-sm text-slate-900 mt-1 font-medium">
                    {control.evidence_required ? control.evidence_required.length : 0} items required
                  </p>
                </div>

                <div>
                  <Label className="text-sm font-medium text-slate-700">Created</Label>
                  <p className="text-sm text-slate-900 mt-1">
                    {formatDateTime(control.created_at)}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      
      {/* Mark Not Required — Remark Dialog */}
      <Dialog
        open={!!naDialog}
        onOpenChange={(open) => {
          if (!open && togglingEvidence !== naDialog?.evidence_text) {
            setNaDialog(null);
            setNaRemark('');
          }
        }}
      >
        <DialogContent className="sm:max-w-md" data-testid="evidence-na-remark-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Ban className="w-5 h-5 text-slate-500" />
              <span>Mark as Not Required</span>
            </DialogTitle>
            <DialogDescription>
              {naDialog?.evidence_text ? (
                <>
                  This action will exclude{' '}
                  <span className="font-medium text-slate-800">
                    “{naDialog.evidence_text}”
                  </span>{' '}
                  from this control's evidence requirements.
                </>
              ) : (
                'Confirm this evidence is not required.'
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="py-2">
            <Label className="text-sm font-medium text-slate-700">
              Reason / Remark <span className="text-red-500">*</span>
            </Label>
            <Textarea
              value={naRemark}
              onChange={(e) => setNaRemark(e.target.value)}
              placeholder="Explain why this evidence is not applicable for this control (e.g. 'Org has no on-prem servers; control covered by cloud-only stack')."
              rows={4}
              className="resize-none mt-2"
              autoFocus
              data-testid="evidence-na-remark-input"
            />
            <p className="text-xs text-slate-500 mt-2">
              The remark is mandatory and will be saved with your name and timestamp on the control.
            </p>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setNaDialog(null);
                setNaRemark('');
              }}
              disabled={togglingEvidence === naDialog?.evidence_text}
              data-testid="evidence-na-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={submitNaDialog}
              disabled={
                !naRemark.trim() ||
                togglingEvidence === naDialog?.evidence_text
              }
              className="bg-slate-700 hover:bg-slate-800 text-white"
              data-testid="evidence-na-submit-btn"
            >
              {togglingEvidence === naDialog?.evidence_text ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…
                </>
              ) : (
                <>
                  <Ban className="w-4 h-4 mr-2" /> Mark as Not Required
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Owner Dialog */}
      <Dialog open={showAssignReviewer} onOpenChange={setShowAssignReviewer}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <UserPlus className="w-5 h-5 text-blue-600" />
              <span>Assign Owner</span>
            </DialogTitle>
            <DialogDescription>
              Select a team member to own this control and its evidence.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Label className="text-sm font-medium text-slate-700 mb-2 block">
              Select Owner
            </Label>
            {(() => {
              const selectedEmp = employees.find((e) => e.id === selectedReviewer);
              return (
                <Popover open={ownerPopoverOpen} onOpenChange={setOwnerPopoverOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      role="combobox"
                      aria-expanded={ownerPopoverOpen}
                      data-testid="assign-owner-combobox-trigger"
                      className="w-full flex items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                    >
                      {selectedEmp ? (
                        <span className="flex items-center gap-2 min-w-0">
                          <span className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-xs font-medium text-blue-700 flex-shrink-0">
                            {(selectedEmp.full_name || selectedEmp.name || selectedEmp.email || '?').charAt(0).toUpperCase()}
                          </span>
                          <span className="truncate">
                            <span className="font-medium">{selectedEmp.full_name || selectedEmp.name || 'Unknown'}</span>
                            <span className="text-slate-500 ml-2 text-xs">{selectedEmp.email}</span>
                          </span>
                        </span>
                      ) : (
                        <span className="text-slate-400">Choose a team member...</span>
                      )}
                      <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0 ml-2" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start" data-testid="assign-owner-combobox-content">
                    <Command
                      filter={(value, search) => {
                        if (!search) return 1;
                        return value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
                      }}
                    >
                      <CommandInput placeholder="Search by name or email..." data-testid="assign-owner-search" />
                      <CommandEmpty>No matches.</CommandEmpty>
                      <CommandList className="max-h-64">
                        <CommandGroup>
                          {employees.length === 0 && (
                            <div className="px-3 py-2 text-sm text-slate-500">No active team members found</div>
                          )}
                          {employees.map((employee) => {
                            const label = `${employee.full_name || employee.name || 'Unknown'} ${employee.email || ''}`;
                            return (
                              <CommandItem
                                key={employee.id}
                                value={label}
                                onSelect={() => { setSelectedReviewer(employee.id); setOwnerPopoverOpen(false); }}
                                data-testid={`assign-owner-option-${employee.id}`}
                                className="flex items-center gap-2 cursor-pointer"
                              >
                                <span className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-xs font-medium text-blue-700 flex-shrink-0">
                                  {(employee.full_name || employee.name || employee.email || '?').charAt(0).toUpperCase()}
                                </span>
                                <span className="min-w-0">
                                  <span className="font-medium">{employee.full_name || employee.name || 'Unknown'}</span>
                                  <span className="text-slate-500 ml-2 text-xs">{employee.email}</span>
                                </span>
                                {employee.id === selectedReviewer && (
                                  <Check className="w-4 h-4 text-navy-600 ml-auto" />
                                )}
                              </CommandItem>
                            );
                          })}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              );
            })()}
            
            {control?.reviewer_email && (
              <p className="text-xs text-slate-500 mt-2">
                Currently assigned to: <span className="font-medium">{control.reviewer_name || control.reviewer_email}</span>
              </p>
            )}
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            {control?.reviewer_id && (
              <Button
                variant="destructive"
                onClick={handleRemoveReviewer}
                disabled={removingReviewer || assigningReviewer}
                className="w-full sm:w-auto sm:mr-auto"
              >
                {removingReviewer ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Removing...
                  </>
                ) : (
                  <>
                    <UserMinus className="w-4 h-4 mr-2" />
                    Remove Owner
                  </>
                )}
              </Button>
            )}
            <div className="flex gap-2 w-full sm:w-auto">
              <Button
                variant="outline"
                onClick={() => setShowAssignReviewer(false)}
                disabled={assigningReviewer || removingReviewer}
                className="flex-1 sm:flex-none"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAssignReviewer}
                disabled={assigningReviewer || removingReviewer || !selectedReviewer || selectedReviewer === 'no-employees'}
                className="bg-blue-600 hover:bg-blue-700 flex-1 sm:flex-none"
              >
                {assigningReviewer ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Assigning...
                  </>
                ) : (
                  'Assign Owner'
                )}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={(open) => {
        setShowDeleteConfirm(open);
        if (!open) setDeleteConfirmText('');
      }}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              <span>Delete Control</span>
            </DialogTitle>
            <DialogDescription>
              This will move the control to deleted state. You can restore it later if needed.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-700 font-medium mb-2">
                You are about to delete:
              </p>
              <p className="text-sm text-red-900 font-semibold">
                {control?.control_name || control?.name}
              </p>
            </div>
            
            <div>
              <Label className="text-sm font-medium text-slate-700">
                Type <span className="font-bold text-red-600">DELETE</span> to confirm
              </Label>
              <Input
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value.toUpperCase())}
                placeholder="Type DELETE to confirm"
                className="mt-2"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowDeleteConfirm(false);
                setDeleteConfirmText('');
              }}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDeleteControl}
              disabled={deleteConfirmText !== 'DELETE' || deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Control
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mandatory reason prompt when flipping status → Not Applicable */}
      <NaReasonDialog
        open={statusNaDialog}
        controlName={control?.control_name}
        onCancel={() => setStatusNaDialog(false)}
        onConfirm={async (reason) => {
          await updateControlStatus('not_applicable', reason);
          setStatusNaDialog(false);
        }}
      />

      {/* Link an existing policy as evidence for a specific requirement slot */}
      <AttachPolicyDialog
        open={!!attachPolicyDialog}
        auditId={control?.audit_id}
        controlId={controlId}
        evidenceIndex={attachPolicyDialog?.evidenceIndex}
        controlFrameworks={control?.applicable_frameworks?.length ? control.applicable_frameworks : (control?.framework ? [control.framework] : [])}
        onClose={() => setAttachPolicyDialog(null)}
        onAttached={() => setFileRefreshTrigger(prev => prev + 1)}
      />

      {/* Generate a fresh evidence artifact with AI for a requirement slot */}
      <AIEvidenceDialog
        open={!!aiEvidenceDialog}
        auditId={control?.audit_id}
        controlId={controlId}
        evidenceIndex={aiEvidenceDialog?.evidenceIndex}
        evidenceText={aiEvidenceDialog?.evidenceText}
        defaultCompanyName={user?.company_name || ''}
        onClose={() => setAiEvidenceDialog(null)}
        onApproved={() => setFileRefreshTrigger(prev => prev + 1)}
      />

      {/* Per-slot "How do I collect this?" structured guidance */}
      <EvidenceGuideDialog
        open={!!evidenceGuideDialog}
        auditId={control?.audit_id}
        controlId={controlId}
        evidenceIndex={evidenceGuideDialog?.evidenceIndex}
        evidenceText={evidenceGuideDialog?.evidenceText}
        onClose={() => setEvidenceGuideDialog(null)}
      />
    </div>
  );
};

export default ControlDetail;